import * as React from 'react';
import { BandwidthManager, BandwidthConfig, DEFAULT_BANDWIDTH_CONFIG } from 'utils/network/bandwidthManager';
import logger from 'utils/log';

export interface BandwidthOptimisationConfig extends Partial<BandwidthConfig> {
  enabled?: boolean;
  streamId?: string;
}

export interface BandwidthOptimisationState {
  shouldBeActive: boolean;
  isManaged: boolean;
}

const generateStreamId = () => `stream-${crypto.randomUUID()}`;

export function useBandwidthOptimisation(config: BandwidthOptimisationConfig = {}): BandwidthOptimisationState {
  const { enabled = true, streamId, ...bandwidthConfig } = config;

  const [shouldBeActive, setShouldBeActive] = React.useState(true);
  const managerRef = React.useRef<BandwidthManager | null>(null);

  const streamIdRef = React.useRef(streamId || generateStreamId());
  const isRegisteredRef = React.useRef(false);

  React.useEffect(() => {
    if (streamId) {
      streamIdRef.current = streamId;
    }
    // We don't want this to change on re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!enabled || isRegisteredRef.current) return;

    try {
      const fullConfig = { ...DEFAULT_BANDWIDTH_CONFIG, ...bandwidthConfig };

      const manager = BandwidthManager.getInstance(fullConfig, {
        onStreamStateChange: (managedStreamId, active) => {
          logger.debug(
            `[BANDWIDTH - DEBUG] Stream state changed: ${managedStreamId} is now ${active ? 'active' : 'inactive'}`
          );
          if (managedStreamId === streamIdRef.current) {
            setShouldBeActive(active);
          }
        },
        onError: (error) => {
          logger.error('[BANDWIDTH] Bandwidth management error', error);
        },
      });

      managerRef.current = manager;

      manager.registerStream(streamIdRef.current);
      isRegisteredRef.current = true;

      setShouldBeActive(document.visibilityState === 'visible');
    } catch (error) {
      logger.error('[BANDWIDTH] Failed to initialize bandwidth manager', error);
    }

    return () => {
      if (managerRef.current && isRegisteredRef.current) {
        managerRef.current.unregisterStream(streamIdRef.current);
        isRegisteredRef.current = false;
      }
    };
    // We want to run this effect only when enabled changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    shouldBeActive: enabled ? shouldBeActive : true,
    isManaged: enabled && !!managerRef.current,
  };
}
